import React, { useState, useEffect } from 'react';
import AcademyHeader from '../../../components/LP/AcademyHeader';
import LPFooter from '../../../components/LP/LPFooter';
import SEO from '../../../components/SEO/SEO';

const BookingPage = () => {
  const [slots, setSlots] = useState([]);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [formData, setFormData] = useState({ name: '', email: '', message: '' });
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    fetch('/data/booking-slots.json?t=' + Date.now())
      .then((r) => r.ok ? r.json() : [])
      .then((data) => {
        const today = new Date().toISOString().split('T')[0];
        const available = data.filter((s) => !s.booked && s.date >= today);
        available.sort((a, b) => (a.date + a.startTime).localeCompare(b.date + b.startTime));
        setSlots(available);
      })
      .catch(() => setSlots([]));
  }, []);

  const formatDate = (dateStr) => {
    const d = new Date(dateStr);
    const days = ['日', '月', '火', '水', '木', '金', '土'];
    return `${d.getFullYear()}年${d.getMonth() + 1}月${d.getDate()}日（${days[d.getDay()]}）`;
  };

  // 日付でグループ化
  const grouped = {};
  slots.forEach((slot) => {
    if (!grouped[slot.date]) grouped[slot.date] = [];
    grouped[slot.date].push(slot);
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!selectedSlot || !formData.name || !formData.email) return;
    setSubmitting(true);

    try {
      const res = await fetch('/api/booking.php', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          slotId: selectedSlot.id,
          date: selectedSlot.date,
          startTime: selectedSlot.startTime,
          endTime: selectedSlot.endTime,
          ...formData,
        }),
      });
      const data = await res.json();
      if (data.success) {
        setResult('success');
      } else {
        setResult('error');
      }
    } catch {
      setResult('error');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col font-noto bg-lp-cream text-lp-charcoal">
      <SEO
        title="個別相談を予約する｜SrWithアカデミー"
        description="SrWithアカデミーの無料個別相談をご予約いただけます。"
      />

      <AcademyHeader />

      <main className="flex-grow pt-[48px]">
        <section className="relative bg-lp-charcoal overflow-hidden">
          <div className="relative max-w-[860px] mx-auto px-5 py-16 md:py-20 text-center">
            <h1 className="font-noto-serif text-2xl md:text-4xl font-bold text-white mb-4">
              個別相談を予約する
            </h1>
            <p className="text-white/70 text-sm md:text-base leading-relaxed max-w-[540px] mx-auto">
              ご都合の良い日時をお選びください。相談は無料です。
            </p>
          </div>
        </section>

        <section className="py-16 md:py-20">
          <div className="max-w-[600px] mx-auto px-5">
            {result === 'success' ? (
              <div className="bg-white rounded-2xl p-8 md:p-12 text-center shadow-lg">
                <div className="text-4xl mb-4">&#10003;</div>
                <h2 className="font-noto-serif text-xl md:text-2xl font-bold text-lp-charcoal mb-4">
                  ご予約ありがとうございます
                </h2>
                <p className="text-sm text-lp-charcoal-mid leading-relaxed mb-2">
                  {formatDate(selectedSlot.date)} {selectedSlot.startTime} - {selectedSlot.endTime}
                </p>
                <p className="text-sm text-lp-charcoal-mid leading-relaxed mb-8">
                  ご登録のメールアドレスに確認メールをお送りしました。
                </p>
                <a
                  href="/lp/academy"
                  className="inline-block px-8 py-3 bg-lp-rose text-white font-medium rounded-lg hover:opacity-90 transition-opacity text-sm"
                >
                  アカデミーページに戻る
                </a>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-6">
                {/* 日時選択 */}
                <div className="bg-white rounded-2xl p-6 md:p-8 shadow-lg">
                  <h3 className="font-bold text-lg mb-4">日時を選択</h3>
                  {Object.keys(grouped).length === 0 ? (
                    <p className="text-sm text-gray-500 text-center py-4">現在空きがありません。後日改めてご確認ください。</p>
                  ) : (
                    <div className="space-y-4">
                      {Object.entries(grouped).map(([date, dateSlots]) => (
                        <div key={date}>
                          <p className="text-sm font-bold text-lp-charcoal mb-2">{formatDate(date)}</p>
                          <div className="flex flex-wrap gap-2">
                            {dateSlots.map((slot) => (
                              <button
                                key={slot.id}
                                type="button"
                                onClick={() => setSelectedSlot(slot)}
                                className={`px-4 py-2 rounded-lg text-sm font-bold transition-all ${
                                  selectedSlot?.id === slot.id
                                    ? 'bg-lp-rose text-white shadow-md'
                                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                                }`}
                              >
                                {slot.startTime} - {slot.endTime}
                              </button>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* フォーム */}
                {selectedSlot && (
                  <div className="bg-white rounded-2xl p-6 md:p-8 shadow-lg space-y-4">
                    <div className="bg-lp-rose/5 rounded-lg p-3 text-center">
                      <p className="text-sm font-bold text-lp-charcoal">
                        {formatDate(selectedSlot.date)} {selectedSlot.startTime} - {selectedSlot.endTime}
                      </p>
                    </div>

                    <div>
                      <label className="block text-sm font-bold text-lp-charcoal mb-2">お名前 *</label>
                      <input
                        type="text"
                        required
                        value={formData.name}
                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                        className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-lp-rose focus:outline-none text-sm"
                        placeholder="例: 田中太郎"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-bold text-lp-charcoal mb-2">メールアドレス *</label>
                      <input
                        type="email"
                        required
                        value={formData.email}
                        onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                        className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-lp-rose focus:outline-none text-sm"
                        placeholder="例: info@example.com"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-bold text-lp-charcoal mb-2">相談内容・ご質問（任意）</label>
                      <textarea
                        value={formData.message}
                        onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                        rows={3}
                        className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-lp-rose focus:outline-none text-sm resize-none"
                      />
                    </div>

                    {result === 'error' && (
                      <p className="text-red-600 text-sm text-center">予約に失敗しました。時間をおいて再度お試しください。</p>
                    )}

                    <button
                      type="submit"
                      disabled={submitting}
                      className="w-full py-4 bg-lp-rose text-white font-bold rounded-lg hover:opacity-90 transition-opacity disabled:opacity-50 text-sm"
                    >
                      {submitting ? '予約中...' : 'この日時で予約する'}
                    </button>
                  </div>
                )}
              </form>
            )}
          </div>
        </section>
      </main>

      <LPFooter />
    </div>
  );
};

export default BookingPage;
